import React, { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import { Actions } from './pages/submissions-page/common/types/Actions';
import { ErrorBoundary } from './common/ErrorBoundary';

const AccountMapPage = lazy(
  () => import('./pages/account-map-page/AccountMapPage'),
);
const ActivateRulesPage = lazy(() => import('./pages/activate-rules-page/'));
const BookingDetailsPage = lazy(() => import('./pages/booking-details-page'));
const CalculationDetailsPage = lazy(
  () => import('./pages/calculation-details-page/CalculationDetailsPage'),
);
const DownloadDataFile = lazy(
  () => import('./pages/download-data-file/DownloadDataFile'),
);
const CreateBookingPage = lazy(() => import('./pages/create-booking-page'));
const ExecutionDetailsTab = lazy(
  () => import('./pages/rule-details-page/tabs/ExecutionDetailsTab'),
);
const InputCorrectionTab = lazy(
  () => import('./pages/rule-details-page/tabs/InputCorrectionTab'),
);
const ExportsPage = lazy(() => import('./pages/exports-page/ExportsPage'));
const GeneralLedger = lazy(
  () => import('./pages/general-ledger-page/GeneralLedger'),
);
const GeneralLedgerPage = lazy(() => import('./pages/general-ledger-page'));
const LandingPage = lazy(() => import('./pages/landing-page/LandingPage'));
const LocationMapPage = lazy(
  () => import('./pages/location-map-page/LocationMapPage'),
);
const ManageBookingsPage = lazy(
  () => import('./pages/manage-bookings-page/ManageBookingsPage'),
);
const ManageRulesPage = lazy(() => import('./pages/manage-rules-page'));
const PermissionDetailsTab = lazy(
  () => import('./pages/rule-details-page/tabs/PermissionDetailsTab'),
);
const PrivateRoute = lazy(() => import('./common/PrivateRoute'));
const RuleDetailsPage = lazy(() => import('./pages/rule-details-page'));
const RuleDetailsTab = lazy(
  () => import('./pages/rule-details-page/tabs/RuleDetailsTab'),
);
const RulePage = lazy(() => import('./pages/configure-rule-page'));
const ScheduleDetailsTab = lazy(
  () => import('./pages/rule-details-page/tabs/ScheduleDetailsTab'),
);
const SubmissionsPage = lazy(
  () => import('./pages/submissions-page/SubmissionsPage'),
);
const SubmissionsTab = lazy(
  () => import('./pages/submissions-page/tabs/SubmissionsTab'),
);
const TransferRulesPage = lazy(() => import('./pages/transfer-rules-page'));
const UpdateBookingPage = lazy(() => import('./pages/update-booking-page'));
const VersionDetailsTab = lazy(
  () => import('./pages/rule-details-page/tabs/VersionDetailsTab'),
);
const ViewPage = lazy(() => import('./pages/view-page/ViewPage'));
const OrganizationPage = lazy(() => import('./pages/organization-page/OrganizationPage'));
const OrganizationDetailsPage = lazy(() => import('./pages/organization-details-page/OrganizationDetailsPage'));

/**
 * App routes
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <PrivateRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'submission',
        element: <SubmissionsPage />,
        children: [
          {
            path: 'all-submissions',
            element: <SubmissionsTab action={Actions.ALL_SUBMISSIONS} />,
          },
        ],
      },
      {
        path: 'general/views',
        element: <ViewPage />,
      },
      {
        path: 'system-admin/organizations',
        element: <OrganizationPage />,
      },
      {
        path: 'system-admin/organizations/:organizationId',
        element: <OrganizationDetailsPage />,
      },
      {
        path: 'general/dataset-exports',
        element: <ExportsPage />,
      },
      {
        path: 'rules/:ruleId',
        element: <RuleDetailsPage />,
        children: [
          {
            index: true,
            element: <RuleDetailsTab />,
          },
          {
            path: 'execution-details',
            element: <ExecutionDetailsTab />,
          },
          {
            path: 'version-details',
            element: <VersionDetailsTab />,
          },
          {
            path: 'schedule-details',
            element: <ScheduleDetailsTab />,
          },
          {
            path: 'permission-details',
            element: <PermissionDetailsTab />,
          },
          {
            path: 'input-correction',
            element: <InputCorrectionTab />,
          },
        ],
      },
      {
        path: 'submission/create-rule',
        children: [
          { index: true, element: <RulePage /> },
          { path: ':ruleId', element: <RulePage /> },
        ],
      },
      {
        path: 'general/manage-rules',
        element: <ManageRulesPage />,
      },
      {
        path: 'general/activate-rules',
        element: <ActivateRulesPage />,
      },
      {
        path: 'general/transfer-rules',
        element: <TransferRulesPage />,
      },
      {
        path: 'general',
        element: <GeneralLedgerPage />,
        children: [
          {
            path: 'ledger',
            element: <GeneralLedger />,
          },
        ],
      },
      {
        path: 'static',
        children: [
          { path: 'account', element: <AccountMapPage /> },
          { path: 'location', element: <LocationMapPage /> },
        ],
      },
      {
        path: 'bookings',
        children: [
          {
            path: 'create',
            element: <CreateBookingPage />,
          },
          { path: 'manage', element: <ManageBookingsPage /> },
          {
            path: ':bookingId',
            children: [
              {
                index: true,
                element: <BookingDetailsPage />,
              },
              {
                path: 'edit',
                element: <UpdateBookingPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'journal-entries/:jeId/calculation-details',
        element: <CalculationDetailsPage />,
      },
      {
        path: 'download-data-file',
        element: <DownloadDataFile />,
      },
    ],
  },
];
